/**
 * シフト提出(2026-09-12 新設)。本人が「その月のどの日に何時から何時まで入れるか」を出す。
 *
 * 1人1か月 = `shift_submissions/{month}_{op_id}`。日ごとに 開始・終了(0.5h刻み)。休みの日は持たない。
 * **提出されたものを確定とする**(2026-09-22 発注者回答。P-45 → D-130)。
 *  - 提出シフト時間 = 本人が出した時間(管理者が直した日は、変更履歴の最初の「変更前」)
 *  - 確定(修正)シフト時間 = このドキュメントのいまの値(管理者の修正・承認した変更申請を反映した後)
 *  - 提出した後、本人はここを直せない(変更は「変更申請」から。firestore.rules でも止める)
 *  - 管理者が直したときは本人にお知らせを出す(shiftNoticeRepo)
 * 出勤表(`shifts/{date}`)はその日の席・割振を決めるもので、確定の時間には使わない。
 */
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where, documentId, orderBy } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { MonthStr } from '@core/lib/types';

/**
 * 日(1〜31) → その日の希望。入れない日はキーを持たない。
 * `bb_id` は出勤形態×報酬区分。**1日に入れられるのは1つだけ**(品川と在宅を同じ日に出せない。2026-09-12 指示)
 */
export type SubmitDay = {
  bb_id?: string;
  start: number;
  end: number;
  /** 欠勤・当日欠勤。入っている日は稼働時間に数えない(2026-09-12) */
  status?: 'absent' | 'same_day';
  /** 休憩(分)。15分単位・60分まで(2026-09-22 D-126)。未入力なら 6時間以上で60分・それ未満は0分 */
  break_min?: number;
  /** 中抜け(時間)。**開始・終了から自動で出す**(2026-09-25)。1時間以上・30分単位(D-126) */
  gap_hours?: number;
  /** 中抜けの開始・終了(0.5きざみの時。2026-09-25 発注者指示「何時から何時までを選択」) */
  gap_from?: number;
  gap_to?: number;
  /** 備考。**中抜けがある日は「何時から何時まで中抜けするか」を必ず書く**(D-126) */
  note?: string;
};

/** 休憩の選択肢(分) */
export const BREAK_MINUTES = [0, 15, 30, 45, 60] as const;
/** 中抜けの選択肢(時間)。1時間から30分単位 */
export const GAP_HOURS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6] as const;

/** 中抜けの時刻の選択肢(8:00〜21:00 の30分きざみ) */
export const GAP_TIMES: number[] = Array.from({ length: 27 }, (_, i) => 8 + i * 0.5);

/** 「12:00」の形 */
export const hhmmOf = (h: number): string => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

/** 中抜けの表示(「12:00〜14:00(2h)」)。開始・終了が無ければ時間だけ */
export function gapLabel(d: Pick<SubmitDay, 'gap_from' | 'gap_to' | 'gap_hours'>): string {
  const hours = gapHoursOf(d);
  if (hours <= 0) return '';
  return d.gap_from !== undefined && d.gap_to !== undefined ? `${hhmmOf(d.gap_from)}〜${hhmmOf(d.gap_to)}(${hours}h)` : `${hours}h`;
}

/** 中抜けの時間(h)。開始・終了があればそこから出す */
export function gapHoursOf(d: Pick<SubmitDay, 'gap_from' | 'gap_to' | 'gap_hours'>): number {
  if (d.gap_from !== undefined && d.gap_to !== undefined && d.gap_to > d.gap_from) return Math.round((d.gap_to - d.gap_from) * 2) / 2;
  return d.gap_hours ?? 0;
}

/** 休憩の既定値: 勤務が6時間以上なら60分、それ未満は0分(2026-09-22 発注者指示) */
export function defaultBreakMin(d: Pick<SubmitDay, 'start' | 'end'>): number {
  return d.end - d.start >= 6 ? 60 : 0;
}

/** その日の休憩(分)。未入力なら既定値 */
export function breakMinOf(d: Pick<SubmitDay, 'start' | 'end' | 'break_min'>): number {
  return d.break_min ?? defaultBreakMin(d);
}

/**
 * 開始・終了を変えたときのその日。**休憩が既定値のままだった日は、新しい時間の既定値に付け直す**
 * (9-18 の既定60分のまま 13-18 に直すと 4時間になってしまうため。2026-09-22)。本人が選んだ休憩・中抜け・備考はそのまま
 */
export function withTimes(d: SubmitDay | undefined, start: number, end: number): SubmitDay {
  const next: SubmitDay = { ...(d ?? {}), start, end };
  if (d && d.break_min !== undefined && d.break_min === defaultBreakMin(d)) delete next.break_min;
  return next;
}

/** 中抜けがあるのに備考が空の日(提出を止める) */
export function missingGapNotes(days: Record<string, SubmitDay>): string[] {
  return Object.entries(days)
    .filter(([, d]) => gapHoursOf(d) > 0 && !(d.note ?? '').trim())
    .map(([k]) => k)
    .sort();
}

/**
 * 変更の記録(誰がいつ、どの日を、どう変えたか)。
 * `before_hours` = 変更前のその日の時間(休憩・中抜けを引いた後。2026-09-22 から記録。D-127)。
 * これが無い古い記録は「変更前」の文字(9-18 など)から、休憩は既定値で数え直す
 */
export type ShiftChangeLog = { day: string; before: string; after: string; by: string; at: string; before_hours?: number };

export type ShiftSubmission = {
  id: string; // = `${month}_${op_id}`
  month: MonthStr;
  op_id: string;
  name: string;
  /** 出勤形態×報酬区分。複数持っている人はどれか1つだけ選ぶ(2026-09-12 指示) */
  bb_id: string;
  days: Record<string, SubmitDay>;
  note: string;
  /** 提出後の変更履歴(新しい順) */
  logs?: ShiftChangeLog[];
  submitted_at?: unknown;
  submitted_by?: string;
};

export const submissionId = (month: MonthStr, opId: string) => `${month}_${opId}`;

/** 15分単位に丸める(休憩が15分単位のため。0.5h に丸めると45分休憩などがずれる) */
export const roundQuarter = (h: number): number => Math.round(h * 4) / 4;

/**
 * その日の提出時間(h)= (終了 − 開始) − 休憩 − 中抜け(2026-09-22 発注者回答「引いてください」。D-127)。
 * 休憩の未入力は既定値(6時間以上で60分)。欠勤・当日欠勤は0
 */
export function dayHours(d: SubmitDay | undefined): number {
  if (!d || d.status) return 0;
  const span = d.end - d.start;
  const brk = (d.break_min ?? defaultBreakMin(d)) / 60;
  const gap = gapHoursOf(d);
  return Math.max(0, roundQuarter(span - brk - gap));
}

export function submittedHours(days: Record<string, SubmitDay>): number {
  let h = 0;
  for (const d of Object.values(days)) h += dayHours(d);
  return roundQuarter(h);
}

/** 保存する形にそろえる(0.5h刻み・休憩の既定値・中抜け・備考の長さ)。読み込み時も同じ形にする */
export function normalizeDays(days: Record<string, SubmitDay>): Record<string, SubmitDay> {
  const out: Record<string, SubmitDay> = {};
  for (const [k, v] of Object.entries(days ?? {})) {
    if (!v || typeof v.start !== 'number' || typeof v.end !== 'number') continue;
    const start = Math.round(v.start * 2) / 2;
    const end = Math.round(v.end * 2) / 2;
    if (end <= start) continue;
    const base: SubmitDay = { start, end };
    if (v.bb_id) base.bb_id = v.bb_id;
    if (v.status === 'absent' || v.status === 'same_day') base.status = v.status;
    // 休憩・中抜け・備考(D-126)。休憩は未入力なら既定値を入れて保存する(あとで見返したときに分かるように)
    const brk = Number(v.break_min ?? defaultBreakMin({ start, end }));
    if (Number.isFinite(brk) && brk > 0) base.break_min = Math.min(60, Math.max(0, Math.round(brk / 15) * 15));
    else base.break_min = 0;
    // 中抜け: 開始・終了があればそこから時間を出す(2026-09-25)
    const from = Number(v.gap_from);
    const to = Number(v.gap_to);
    if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
      base.gap_from = Math.round(from * 2) / 2;
      base.gap_to = Math.round(to * 2) / 2;
      base.gap_hours = Math.round((base.gap_to - base.gap_from) * 2) / 2;
    } else {
      const gap = Number(v.gap_hours ?? 0);
      if (Number.isFinite(gap) && gap >= 1) base.gap_hours = Math.round(gap * 2) / 2;
    }
    if (typeof v.note === 'string' && v.note.trim()) base.note = v.note.trim().slice(0, 200);
    out[String(Number(k)).padStart(2, '0')] = base;
  }
  return out;
}

export async function fetchSubmission(month: MonthStr, opId: string): Promise<ShiftSubmission | null> {
  const snap = await getDoc(doc(getDb(), 'shift_submissions', submissionId(month, opId)));
  if (!snap.exists()) return null;
  const d = snap.data() as Omit<ShiftSubmission, 'id'>;
  return { id: snap.id, ...d, days: normalizeDays(d.days) };
}

/** その月の全員分 */
export async function fetchSubmissions(month: MonthStr): Promise<ShiftSubmission[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'shift_submissions'), where(documentId(), '>=', `${month}_`), where(documentId(), '<=', `${month}_`), orderBy(documentId())),
  );
  return snap.docs.map((d) => {
    const v = d.data() as Omit<ShiftSubmission, 'id'>;
    return { id: d.id, ...v, days: normalizeDays(v.days) };
  });
}

export type SaveSubmissionArgs = {
  month: MonthStr;
  opId: string;
  name: string;
  bbId: string;
  days: Record<string, SubmitDay>;
  note: string;
  userId: string;
  logs?: ShiftChangeLog[];
};

/** 保存するドキュメントの中身(1回の書き込みにまとめたいとき、batch に渡す) */
export function submissionData(args: SaveSubmissionArgs) {
  const { month, opId, name, bbId, days, note, userId, logs } = args;
  return {
    month,
    op_id: opId,
    name,
    bb_id: bbId,
    days: normalizeDays(days),
    note: (note ?? '').trim(),
    logs: (logs ?? []).slice(0, 200),
    submitted_at: serverTimestamp(),
    submitted_by: userId,
  };
}

/** 保存する(本人の最初の提出・管理者の代理の最初の提出)。管理者が直すときは shiftNoticeRepo.saveSubmissionByAdmin */
export async function saveSubmission(args: SaveSubmissionArgs): Promise<void> {
  await setDoc(doc(getDb(), 'shift_submissions', submissionId(args.month, args.opId)), submissionData(args));
}
